import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { useIntegratorMe } from "@/lib/me-query";
import { MentionTextarea } from "@/components/MentionTextarea";
import { Button } from "@/components/ui/Button";
import { CardTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { MANUFACTURING_HUB_READ_ONLY } from "@/pages/manufacturing/workOrdersPageModel";
import { formatDateTime } from "@/pages/manufacturing/workOrdersPageModel";
import { WIRELESS_ISSUE_QUERY_KEY } from "@/pages/manufacturing/wirelessIssueApi";

export interface WirelessIssuePcbaNoteRow {
  id: string;
  note_text: string;
  created_at: string;
  created_by_email: string;
  created_by_display_name: string | null;
  deleted_at: string | null;
}

function noteAuthorLabel(note: WirelessIssuePcbaNoteRow): string {
  const displayName = note.created_by_display_name?.trim();
  if (displayName != null && displayName !== "") {
    return displayName;
  }
  return note.created_by_email;
}

function noteAuthorEmailsMatch(authorEmail: string, userEmail: string): boolean {
  return authorEmail.trim().toLowerCase() === userEmail.trim().toLowerCase();
}

function isWirelessIssuePcbaNoteDeleted(note: WirelessIssuePcbaNoteRow): boolean {
  return note.deleted_at != null && note.deleted_at.trim() !== "";
}

function wirelessIssuePcbaNotesPath(pcbaSerial: string): string {
  return `/api/manufacturing/wireless-issue/pcba/${encodeURIComponent(pcbaSerial)}/notes`;
}

export interface WirelessIssuePcbaNotesPanelProps {
  pcbaSerial: string;
  hubWritesAllowed: boolean;
  variant?: "panel" | "compact";
  onPosted?: () => void;
}

export function WirelessIssuePcbaNotesPanel({
  pcbaSerial,
  hubWritesAllowed,
  variant = "panel",
  onPosted,
}: WirelessIssuePcbaNotesPanelProps): JSX.Element {
  const queryClient = useQueryClient();
  const meQuery = useIntegratorMe();
  const currentUserEmail = meQuery.data?.user.email ?? "";
  const [draft, setDraft] = useState("");

  useEffect(() => {
    setDraft("");
  }, [pcbaSerial]);

  const notesQuery = useQuery<{ notes: WirelessIssuePcbaNoteRow[] }, ApiError>({
    queryKey: ["manufacturing", "wireless-issue", "pcba-notes", pcbaSerial],
    queryFn: () => apiFetch<{ notes: WirelessIssuePcbaNoteRow[] }>(wirelessIssuePcbaNotesPath(pcbaSerial)),
    staleTime: 30_000,
  });

  const invalidateNotes = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ["manufacturing", "wireless-issue", "pcba-notes", pcbaSerial],
    });
    void queryClient.invalidateQueries({ queryKey: WIRELESS_ISSUE_QUERY_KEY });
  };

  const postMutation = useMutation<void, ApiError, string>({
    mutationFn: async (noteText) => {
      await apiFetch(wirelessIssuePcbaNotesPath(pcbaSerial), {
        method: "POST",
        body: { note_text: noteText },
      });
    },
    onSuccess: () => {
      setDraft("");
      invalidateNotes();
      onPosted?.();
    },
  });

  const deleteMutation = useMutation<void, ApiError, string>({
    mutationFn: async (noteId) => {
      await apiFetch(`${wirelessIssuePcbaNotesPath(pcbaSerial)}/${noteId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      invalidateNotes();
    },
  });

  const notes = notesQuery.data?.notes ?? [];
  const isCompact = variant === "compact";
  const deletingNoteId = deleteMutation.isPending ? deleteMutation.variables : null;

  const listSection = (
    <div className={isCompact ? "min-h-0 flex-1 overflow-y-auto" : "min-h-0 flex-1 overflow-y-auto"}>
      {notesQuery.isLoading ? (
        <p className="text-sm text-slate-600">Loading notes…</p>
      ) : notesQuery.isError ? (
        <p className="text-sm text-red-700">{notesQuery.error.message}</p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-slate-600">No notes yet.</p>
      ) : (
        <ul className="space-y-3">
          {notes.map((note) => {
            const deleted = isWirelessIssuePcbaNoteDeleted(note);
            const isOwnNote =
              currentUserEmail !== "" && noteAuthorEmailsMatch(note.created_by_email, currentUserEmail);
            const canDelete = hubWritesAllowed && isOwnNote && !deleted;
            const isDeleting = deletingNoteId === note.id;

            return (
              <li
                key={note.id}
                className={cn(
                  "rounded-md border border-slate-100 bg-slate-50/80 px-2 py-2 text-sm",
                  deleted && "text-slate-400",
                )}
              >
                <p
                  className={cn(
                    "whitespace-pre-wrap break-words",
                    deleted ? "text-slate-400 line-through decoration-slate-400" : "text-slate-900",
                  )}
                >
                  {note.note_text}
                </p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p
                    className={cn(
                      "min-w-0 text-xs",
                      deleted ? "text-slate-400 line-through decoration-slate-400" : "text-slate-500",
                    )}
                  >
                    {noteAuthorLabel(note)}
                    <span className="text-slate-400"> · </span>
                    {formatDateTime(note.created_at) || "—"}
                  </p>
                  {canDelete ? (
                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 disabled:opacity-50"
                      aria-label="Delete your note"
                      disabled={isDeleting}
                      onClick={() => deleteMutation.mutate(note.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      {isDeleting ? "Deleting…" : "Delete"}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {deleteMutation.isError ? (
        <p className="mt-2 text-xs text-red-700">{deleteMutation.error.message}</p>
      ) : null}
    </div>
  );

  const composeSection = (
    <div className={isCompact ? "shrink-0 border-t border-slate-100 pt-2" : "shrink-0 border-t border-slate-100 px-0 pt-3"}>
      <label className="block text-xs font-medium text-slate-600">New note</label>
      <MentionTextarea
        value={draft}
        onChange={setDraft}
        disabled={!hubWritesAllowed || postMutation.isPending}
        rows={isCompact ? 3 : 4}
        className="mt-1 w-full resize-y rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
        placeholder={
          hubWritesAllowed
            ? "Share context for the team. Type @ to mention someone."
            : "You do not have permission to add notes."
        }
      />
      {postMutation.isError ? (
        <p className="mt-1 text-xs text-red-700">{postMutation.error.message}</p>
      ) : null}
      <ReadOnlyHoverLayer
        active={!hubWritesAllowed}
        hoverHint={MANUFACTURING_HUB_READ_ONLY}
        className="mt-2 inline-block"
      >
        <Button
          type="button"
          disabled={!hubWritesAllowed || draft.trim() === "" || postMutation.isPending}
          onClick={() => postMutation.mutate(draft.trim())}
        >
          {postMutation.isPending ? "Posting…" : "Post note"}
        </Button>
      </ReadOnlyHoverLayer>
    </div>
  );

  if (isCompact) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {listSection}
        {composeSection}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="shrink-0 border-b border-slate-100 px-3 py-2">
        <CardTitleWithHelp hoverHint="Shared notes for hub operators on this PCBA identity. Authors can soft-delete their own notes (strikethrough).">
          PCBA notes
        </CardTitleWithHelp>
        <p className="mt-0.5 font-mono text-sm font-semibold text-slate-900">{pcbaSerial}</p>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden px-3 py-2">{listSection}</div>
      <div className="shrink-0 px-3 pb-3">{composeSection}</div>
    </div>
  );
}

export interface WirelessIssuePcbaNotesPreview {
  text: string;
  createdAt: string;
  authorLabel: string;
  deleted?: boolean;
}

export function wirelessIssuePcbaNotesHoverHint(
  noteCount: number,
  latestNote: WirelessIssuePcbaNotesPreview | null,
): JSX.Element {
  if (noteCount <= 0 || latestNote == null) {
    return (
      <div className="max-w-[18rem] whitespace-normal text-left text-sm leading-snug text-slate-600">
        No notes yet. Open to add shared context for this PCBA.
      </div>
    );
  }
  const deleted = latestNote.deleted === true;
  return (
    <div className="max-w-[22rem] whitespace-normal text-left text-sm leading-snug text-slate-800">
      {noteCount > 1 ? (
        <p className="mb-1.5 border-b border-amber-100 pb-1 text-xs font-semibold text-amber-900">
          {String(noteCount)} notes
        </p>
      ) : null}
      <p
        className={cn(
          "whitespace-pre-wrap break-words",
          deleted && "text-slate-500 line-through decoration-slate-400",
        )}
      >
        {latestNote.text}
      </p>
      <p
        className={cn(
          "mt-1 text-xs text-slate-500",
          deleted && "text-slate-400 line-through decoration-slate-400",
        )}
      >
        {latestNote.authorLabel}
        <span className="text-slate-400"> · </span>
        {formatDateTime(latestNote.createdAt) || "—"}
      </p>
    </div>
  );
}
